import type {
  AppGalleryButtonProps,
  AppGalleryButtonState,
} from "./AppGalleryButton.type";
import {
  useGetId,
  useCheckableRole,
  useOnExecute,
  useSelectableRole,
  useAutomationTypeWithGalleryButtonSuffix,
} from "../../../hooks";

// eslint-disable-next-line no-restricted-imports
import { useAppButton } from "@1js/acui-button/lib/Next";

export const useAppGalleryButton = (
  props: AppGalleryButtonProps,
  ref: React.Ref<HTMLButtonElement>
): AppGalleryButtonState => {
  const { onExecute, ...restProps } = props;
  const state = useAppButton(
    {
      ...restProps,
      id: useGetId(props.id, props.isPreviewGalleryButton),
      onClick: useOnExecute(props),
      onFocus: !props.disabled ? () => props.onFocus?.() : () => {},
      onBlur: !props.disabled ? () => props.onBlur?.() : () => {},
      role: props.role && props.role,
      dataAutomationType: useAutomationTypeWithGalleryButtonSuffix(
        props.automationType
      ),
      fullDescriptionLabel: props.ariaLabel && props.ariaLabel,
      // reduce the potential size of the gallery button to medium and large (gallery buttons can't be small)
      size:
        props.size && props.size.toLowerCase() === "large" ? "large" : "medium",
    },
    ref
  ) as AppGalleryButtonState;

  state.selected = props.selected ?? false;

  state.allowDisabledFocus = props.allowDisabledFocus ?? false;

  state.root["data-unique-id"] =
    props.index !== undefined ? String(props.index) : undefined;

  state.root["aria-selected"] = useSelectableRole(props.role)
    ? props.selected
    : undefined;

  state.root["aria-current"] = useSelectableRole(props.role)
    ? props.selected
    : undefined;

  state.root["aria-checked"] = useCheckableRole(props.role)
    ? props.selected
    : undefined;

  return state;
};
